import React, { useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment, { Moment } from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FlightsClassTypes } from 'constants/flight'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { getCheapestSelectedPackageOptionWithDefault, arePricesLoading, isBNBLAvailable, getFromDurationLabel, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import Divider from 'LuxKit/Divider'
import GeoContext from 'contexts/geoContext'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Tooltip from 'LuxKit/Tooltip'
import getGiftingMessage from 'lib/gifting/getGiftingMessage'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { getPriceDisplayFunction, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Clickable from 'components/Common/Clickable'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { formatGuestsDisplay } from 'lib/string/stringUtils'

const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(12)};
`

const SummaryPane = styled(Pane)`
  position: relative;
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(4)};
  align-items: center;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(8)};
  text-align: center;
`

interface Props {
  offer: App.Offer
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  className?: string
  cheapestSelectedOption: App.OfferPackageOptionView
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  checkIn?: string
  isBNBLAvailable: boolean
  isSpoofed: boolean
  durationLabel: string
  availableForPurchase?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  priceMode: OfferPagePriceMode
}

function FlashOfferSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption: packageView,
    rooms,
    flightDestination,
    calendarsLoading,
    checkIn,
    isBNBLAvailable,
    isSpoofed,
    durationLabel,
    availableForPurchase,
    charterBundleRoute,
    fareClass,
    priceMode,
  } = props

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const dispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const charterRoute = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  const handleScheduleCallback = () => {
    showModal(<RequestAgentCallbackModal type="SALES" />)
  }

  const { totalFlightPrice: charterFlightPrice } = useOfferPageFlightPrice()

  // Charter route: use charter flight price directly (not affected by flightCacheDisabled)
  // Non-charter route: use package flight price if cache is enabled
  const flightPrice = charterRoute
    ? charterFlightPrice // Charter: only use charter price (undefined if not available)
    : !offer.flightCacheDisabled
        ? packageView.flightPrice
        : undefined

  const {
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const pricingWithFlights = flightPrice ? addFlightPrice(hotelPricing, flightPrice) : undefined
  const memberPricingWithFlights = (hotelMemberPricing && flightPrice) ? addFlightPrice(hotelMemberPricing, flightPrice) : undefined

  const bundledWithFlightsOnly = offer.bundledWithFlightsOnly || !!charterRoute

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const hasFlights = !!pricingWithFlights && (!!charterRoute || !offer.flightCacheDisabled)
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])
  const hideNoCacheMessaging = !hasFlights && bundledWithFlightsOnly

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !!checkIn && !!charterDepartDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkIn || !isCheckInMatchingCharterDate)

  // Get fallback pricing for charter bundle when no dates are selected
  // Pass rooms from context - the hook has its own fallback for empty rooms
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass,
    disabled: !shouldUseFallbackPricing,
    packageUniqueKey: pkg.uniqueKey,
  })

  let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
  let isUsingFallbackPricing = false
  if (bundledWithFlightsOnly) {
    if (!charterRoute && flightsEnabled && !hideNoCacheMessaging && hasFlights) {
      // Non-charter route: use standard flight pricing
      pricingToDisplay = pricingWithFlights
      memberPricingToDisplay = memberPricingWithFlights
    }
    else if (charterRoute) {
      if (fallbackPricing) {
        // No valid date selected - show fallback pricing
        pricingToDisplay = fallbackPricing
        memberPricingToDisplay = fallbackMemberPricing
        isUsingFallbackPricing = true
      } else if (hasFlights) {
        // Valid date selected - show actual flight price
        pricingToDisplay = pricingWithFlights
        memberPricingToDisplay = memberPricingWithFlights
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    else {
      pricingToDisplay = undefined
      memberPricingToDisplay = undefined
    }
  }

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0
  const showDiscountPill = !offer?.isDiscountPillHidden && pricingToDisplay && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showPriceDetails = (fees.taxesAndFees ?? 0) > 0 || (fees.property ?? 0) > 0 || config.agentHub.isEnabled

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing.price)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReserveForZeroTagShown = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInMoment)
  const isDepositTagShown = showSecureWithDepositTag
  const shouldShowValue = !!pricingToDisplay && pricingToDisplay.value > pricingToDisplay.price && pkg.shouldDisplayValue
  const isIndiaRegion = currentRegionCode === 'IN'
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'
  const showDisclaimers = (flightsEnabled && !hideNoCacheMessaging) || (isBNBLAvailable && !isMemberOnlyOffer)
  const shouldShowRoomType = !!pkg.roomType?.name

  let saleUnit = pricingToDisplay?.saleUnit ?? offer.saleUnit
  if (bundledWithFlightsOnly) {
    saleUnit = 'package*'
  }

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing.price))
  }, [appDispatch, hotelPricing.price])

  const bookByFormatted = useMemo<Moment | undefined>(() => {
    return offer.bookByDate ? moment(offer.bookByDate) : undefined
  }, [offer.bookByDate])

  const travelUntilFormatted = useMemo<Moment | undefined>(() => {
    return offer.travelToDate ? moment(offer.travelToDate) : undefined
  }, [offer.travelToDate])

  const { partnership } = useContext(GeoContext)

  const viewOptionsLabel = `View ${config.brandConfig.travelSynonym} options`

  const giftingMessage = useMemo<string | undefined>(() => {
    if (bookByFormatted && travelUntilFormatted) {
      return getGiftingMessage({ bookBy: bookByFormatted, travelUntil: travelUntilFormatted })
    }
  }, [bookByFormatted, travelUntilFormatted])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice,
      skip: !bundledWithFlightsOnly || !hasFlights || !flightsEnabled,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled, currentRegionCode, bundledWithFlightsOnly, flightPrice])

  const shouldTrackPrice = availableForPurchase && showPrices && !calendarsLoading && !!pricingToDisplay?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && pricingToDisplay?.price && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: pricingToDisplay.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, pricingToDisplay?.price, offer?.id])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const totalGuests = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const guestsDisplayText = useMemo(() => {
    if (!charterRoute || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests)
  }, [charterRoute, totalGuests])
  return (
    <SummaryPane border="neutral-six" borderRadius="XS" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <Pane padding="28 20">
        <VerticalSpacer gap={12}>
          <Group direction="vertical" gap={16}>
            {availableForPurchase && <WalledContent
              enforced={offer.walledGarden}
              wall={<BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>}
            >
              {!charterRoute && showPrices &&
                <PackagePrices>
                  {isTimeLimited(offer) && <OfferTimeLimitedText />}
                  {isDepositTagShown && !isSpoofed && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
                  {isReserveForZeroTagShown && <ReserveForZeroTag />}
                  <Group direction="vertical" horizontalAlign="center" gap={8}>
                    <Group direction="vertical" horizontalAlign="center">
                      {bundledWithFlightsOnly &&
                        <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
                          Hotel + flights{hideNoCacheMessaging && ' packages available'}
                        </CaptionTextBlock>}
                      {pricingToDisplay && <>
                        <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                        <LuxPlusPriceStack
                          size="L"
                          price={convertToDisplay(pricingToDisplay.price)}
                          memberPrice={memberPricingToDisplay?.price && convertToDisplay(memberPricingToDisplay?.price)}
                          saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                          testid="pricing"
                          horizontalAlign="center"
                          offerConfig={offer.luxPlus}
                        />
                        {bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="neutral-three">
                          &nbsp;Two Adults
                        </Caption>}
                        <PriceRowTaxesAndFees
                          endIcon={showPriceDetails && <OfferPriceDetailsRow
                            offerPage
                            offer={offer}
                            duration={pkg.duration}
                            propertyFees={fees.property}
                            taxesAndFees={fees.taxesAndFees}
                            hotelPrice={priceWithoutPropertyFees(hotelPricing)}
                            hotelMemberPrice={hotelMemberPricing ? priceWithoutPropertyFees(hotelMemberPricing) : undefined}
                            showMemberPrice={showMemberPrice}
                            flightPrice={bundledWithFlightsOnly ? flightPrice : undefined}
                            rooms={rooms}
                            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                            trigger="info-icon"
                            cta={
                              <TextButton
                                kind="primary"
                                fit="flex"
                              >
                                {viewOptionsLabel}
                              </TextButton>
                            }
                            onCtaAfterClose={onViewPackageOptions}
                          />}
                        />
                        {showIncludedGuestsExceeded && (
                          <AdditionalGuestsPopup
                            complex={false}
                            description="+ Extra guest surcharge"
                            modalContent={pkg.roomPolicyDescription}
                          />
                        )}
                        {shouldShowValue && <PriceRowValueDiscountWithCaption
                          data-testid="value-box"
                          size="M"
                          originalValue={convertToDisplay(pricingToDisplay.value)}
                          discountPercentage={showDiscountPill ? pricingToDisplay.discountPercent : undefined}
                        />}
                      </>}
                    </Group>
                    <LoyaltyPointsEarn
                      horizontalAlign="center"
                      calculationRequests={pointsEarnCalculationRequests}
                      calculationType="estimate"
                      offer={offer}
                    />
                  </Group>
                  { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel offer={offer} />}
                  <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
                  <PriceRowAgentHubCommission
                    productType={offer.productType}
                    offerId={offer.id}
                    offerLocations={offerLocations}
                    vendorName={vendorName}
                    packageId={pkg.id}
                  />
                </PackagePrices>}
              {charterRoute && showPrices &&
                <div>
                  <VerticalSpacer gap={16}>
                    <Group direction="vertical" horizontalAlign="start" gap={8}>
                      <Group direction="vertical" horizontalAlign="start">
                        {pricingToDisplay && <>
                          <PriceRowPriceCaption startIcon={<LinePlaneDepartureIcon />}>{pkg.duration < MALDIVES_BUNDLE_DEFAULT_NIGHTS ? MALDIVES_BUNDLE_DEFAULT_NIGHTS : pkg.duration} nights + flights {isUsingFallbackPricing && 'from'}</PriceRowPriceCaption>
                          <LuxPlusPriceStack
                            size="L"
                            price={convertToDisplay(pricingToDisplay.price)}
                            memberPrice={memberPricingToDisplay?.price && convertToDisplay(memberPricingToDisplay?.price)}
                            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                            testid="pricing"
                            horizontalAlign="start"
                            offerConfig={offer.luxPlus}
                          />
                          {shouldShowValue && <PriceRowValueDiscountWithCaption
                            data-testid="value-box"
                            size="L"
                            originalValue={convertToDisplay(pricingToDisplay.value)}
                            discountPercentage={showDiscountPill ? pricingToDisplay.discountPercent : undefined}
                          />}
                          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
                          {shouldShowRoomType && (
                            <Caption variant="medium" colour="neutral-three">
                              {pkg.roomType?.name}
                            </Caption>
                          )}
                        </>}
                      </Group>
                      <LoyaltyPointsEarn
                        horizontalAlign="center"
                        calculationRequests={pointsEarnCalculationRequests}
                        calculationType="estimate"
                        offer={offer}
                      />
                    </Group>
                    <TextButton
                      kind="primary"
                      data-testid="OfferAvailable"
                      onClick={onViewPackageOptions}
                      fit="flex"
                    >
                      {viewOptionsLabel}
                    </TextButton>
                  </VerticalSpacer>
                  <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
                </div>}
              {!charterRoute && <TextButton
                kind="primary"
                data-testid="OfferAvailable"
                onClick={onViewPackageOptions}
                fit="flex"
              >
                {viewOptionsLabel}
              </TextButton>}
              {showDisclaimers && !charterRoute && <Group direction="vertical" gap={8}>
                {flightsEnabled && !hideNoCacheMessaging && <Group direction="vertical" horizontalAlign="center">
                  {!bundledWithFlightsOnly && hasFlights && <PriceRowFlightPackage
                    product="hotel"
                    data-testid="FlightPrice"
                    price={showMemberPrice ? memberPricingWithFlights?.price : pricingWithFlights.price}
                  />}
                  {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                    <ChangeDepartureWrapper>
                      <PriceRowCaption>
                        No flights available from {flightOrigin.name}. Please select another airport.
                      </PriceRowCaption>
                      <TextLink onClick={onToggleFilter} data-filter="airport">
                        Change departure airport
                      </TextLink>
                    </ChangeDepartureWrapper>}
                  {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                    <PriceRowCaption>
                      {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
                      {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
                    </PriceRowCaption>}
                  {!hasFlights && departureTooFarInFuture &&
                    <ChangeDepartureWrapper>
                      <Caption variant="large" colour="neutral-three">
                        Flights can only be booked up to 11 months in advance.
                      </Caption>
                    </ChangeDepartureWrapper>}
                  <AtolProtectedTooltip />
                </Group>}
                {isBNBLAvailable && !isMemberOnlyOffer && <CaptionTextBlock
                  variant="medium"
                  startIcon={<LineGiftIcon />}
                  align="center"
                >
                  Gifting available{' '}
                  <SoftOfferPageWalledGarden>
                    <Tooltip description={giftingMessage}>
                      <TextLink>Learn more</TextLink>
                    </Tooltip>
                  </SoftOfferPageWalledGarden>
                </CaptionTextBlock>}
              </Group>}
            </WalledContent>}
          </Group>
          {!availableForPurchase &&
            <>
              <BodyText
                variant="large"
                weight="bold"
                data-testid="OfferSoldOut"
              >
                This offer has SOLD OUT
              </BodyText>
              <TextButton
                kind="primary"
                to={`/${OFFER_TYPE_HOTEL_SLUG}`}
                fit="flex"
              >
                Browse all hotel offers
              </TextButton>
            </>}
        </VerticalSpacer>
      </Pane>
      {charterRoute &&
        <>
          <Divider kind="secondary" />
          <Pane background="neutral-eight" padding={20}>
            <VerticalSpacer gap={8}>
              <VerticalSpacer gap={0}>
                <BodyText variant="large" weight="bold" align="center">
                  Have questions?
                </BodyText>
                <BodyText variant="medium" align="center">
                  Talk to a Maldives travel expert today.
                </BodyText>
              </VerticalSpacer>
              <VerticalSpacer gap={8}>
                <Clickable
                  href={charterRoute.supportNumber.href}
                  aria-label="Local Sales & Support contact"
                  rel="nofollow"
                >
                  <HeadingTextBlock variant="heading5" startIcon={<LinePhoneIcon />} align="center">
                    {charterRoute.supportNumber.humanReadable}
                  </HeadingTextBlock>
                </Clickable>
                <BodyText variant="medium" align="center">
                  or{' '}
                  <TextLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      handleScheduleCallback()
                    }}
                    underline
                  >
                    schedule a call back
                  </TextLink>
                </BodyText>
              </VerticalSpacer>
            </VerticalSpacer>
          </Pane>
        </>}
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  flightOrigin: state.airport,
  calendarsLoading: arePricesLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  isSpoofed: appState.auth.account.isSpoofed,
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
  charterBundleRoute: state.charterBundleRoute,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
}), FlashOfferSummary, preventRenderWithOpenFilter)
